import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Route, Routes, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import HomePage from './components/HomePage';
import PortfolioPage from './components/PortfolioPage';
import TravelerBackdrop from './components/TravelerBackdrop';
import TravelTutorial from './components/TravelTutorial';
import projectsData from './data/projects.json';
import usePrefersReducedMotion from './hooks/usePrefersReducedMotion';
import './css/App.css';

// Remembers (per browser) that the Traveler Mode tutorial was seen or skipped.
const TUTORIAL_KEY = 'traveler-tutorial:v1';

function isTutorialDone() {
  try {
    return window.localStorage.getItem(TUTORIAL_KEY) === 'done';
  } catch (error) {
    return false;
  }
}

function markTutorialDone() {
  try {
    window.localStorage.setItem(TUTORIAL_KEY, 'done');
  } catch (error) {
    // Storage blocked (private mode etc.): the tutorial may simply show again.
  }
}

const THEME_COLORS = {
  engineer: '#0e1116',
  traveler: '#f3ebda',
};

function CompassIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9.2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M15.4 8.6 13.2 13.2 8.6 15.4 10.8 10.8Z" fill="currentColor" />
      <circle cx="12" cy="12" r="1" fill="var(--color-bg, #0e1116)" />
    </svg>
  );
}

function JstClock() {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(new Date());
    }, 30000);

    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const formatted = useMemo(() => {
    try {
      return new Intl.DateTimeFormat('en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Tokyo',
      }).format(now);
    } catch (error) {
      return '';
    }
  }, [now]);

  if (!formatted) {
    return null;
  }

  return (
    <span className="site-footer__clock" title="Japan Standard Time">
      JST {formatted}
    </span>
  );
}

function App() {
  const [language, setLanguage] = useState('ja');
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefersReducedMotion = usePrefersReducedMotion();

  const mode = searchParams.get('mode') === 'traveler' ? 'traveler' : 'engineer';

  // On entering Traveler Mode the "旅の記録" nav link bursts in, flashes and
  // bounces so nobody can miss it, then calms down. 'wave' waits for the mode
  // switch transition to finish; 'now' plays right away.
  const [travelsDebut, setTravelsDebut] = useState('');
  const debutTimerRef = useRef(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const tutorialTimerRef = useRef(null);
  const enteredViaToggleRef = useRef(false);

  const toggleLanguage = () => {
    setLanguage((prevLanguage) => (prevLanguage === 'ja' ? 'en' : 'ja'));
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-mode', mode);
    const themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) {
      themeMeta.setAttribute('content', THEME_COLORS[mode]);
    }
  }, [mode]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  useEffect(() => {
    // A hint for fellow engineers reading the console.
    // eslint-disable-next-line no-console
    console.log(
      '%c⌖ Every map has hidden pages — try /?mode=traveler',
      'font-family: monospace; color: #f2c94c; background: #0e1116; padding: 4px 8px; border-radius: 4px;'
    );
  }, []);

  const applyMode = useCallback(
    (nextMode) => {
      document.documentElement.setAttribute('data-mode', nextMode);
      const nextParams = new URLSearchParams(searchParams);
      if (nextMode === 'traveler') {
        nextParams.set('mode', 'traveler');
      } else {
        nextParams.delete('mode');
      }
      const search = nextParams.toString();
      // navigate (not setSearchParams) so the current #hash survives the toggle.
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
        { preventScrollReset: true, replace: true }
      );
    },
    [location.hash, location.pathname, navigate, searchParams]
  );

  const toggleMode = useCallback(() => {
    const nextMode = mode === 'traveler' ? 'engineer' : 'traveler';

    enteredViaToggleRef.current = nextMode === 'traveler';

    if (prefersReducedMotion || typeof document.startViewTransition !== 'function') {
      applyMode(nextMode);
      return;
    }

    // The new view sweeps down over the old one like a wave washing the page
    // (see the ::view-transition rules in App.css).
    document.startViewTransition(() => {
      flushSync(() => {
        applyMode(nextMode);
      });
    });
  }, [applyMode, mode, prefersReducedMotion]);

  const startDebut = useCallback(
    (kind) => {
      window.clearTimeout(debutTimerRef.current);
      if (prefersReducedMotion) {
        return;
      }
      setTravelsDebut(kind);
      debutTimerRef.current = window.setTimeout(
        () => setTravelsDebut(''),
        (kind === 'wave' ? 1100 : 250) + 4500
      );
    },
    [prefersReducedMotion]
  );

  // Entering Traveler Mode (by URL or by the compass): first-time visitors get
  // the route-replay tutorial; everyone else gets the nav debut straight away.
  useEffect(() => {
    window.clearTimeout(tutorialTimerRef.current);
    const viaToggle = enteredViaToggleRef.current;
    enteredViaToggleRef.current = false;

    if (mode !== 'traveler' || location.pathname !== '/') {
      setShowTutorial(false);
      setTravelsDebut('');
      return undefined;
    }

    if (isTutorialDone()) {
      startDebut(viaToggle ? 'wave' : 'now');
      return undefined;
    }

    // Let the hero (and the mode-switch wave) settle before the popup.
    tutorialTimerRef.current = window.setTimeout(() => setShowTutorial(true), viaToggle ? 1700 : 1300);
    return () => window.clearTimeout(tutorialTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, location.pathname]);

  const handleTutorialFinish = useCallback(
    (result) => {
      markTutorialDone();
      setShowTutorial(false);
      // Visitors who opened the replay are already where we wanted them.
      if (result === 'skip') {
        startDebut('now');
      }
    },
    [startDebut]
  );

  useEffect(() => {
    return () => {
      window.clearTimeout(debutTimerRef.current);
      window.clearTimeout(tutorialTimerRef.current);
    };
  }, []);

  const sectionMenus = useMemo(() => {
    const base =
      language === 'ja'
        ? [
            { id: 'about', label: '自己紹介' },
            { id: 'journey', label: '歩み' },
            { id: 'works', label: '作品' },
            { id: 'writing', label: '寄稿記事' },
            { id: 'contact', label: '連絡先' },
          ]
        : [
            { id: 'about', label: 'About' },
            { id: 'journey', label: 'Journey' },
            { id: 'works', label: 'Works' },
            { id: 'writing', label: 'Writing' },
            { id: 'contact', label: 'Contact' },
          ];

    // Traveler Mode puts the journeys right below the hero, so they lead the
    // menu too.
    if (mode === 'traveler') {
      base.unshift(
        language === 'ja' ? { id: 'travels', label: '旅の記録' } : { id: 'travels', label: 'Travels' }
      );
    }

    return base;
  }, [language, mode]);

  const scrollToSection = useCallback((sectionId) => {
    if (typeof window === 'undefined') {
      return;
    }

    const target = document.getElementById(sectionId);
    if (!target) {
      return;
    }

    const navElement = document.querySelector('.site-nav');
    const navHeight = navElement ? navElement.getBoundingClientRect().height : 72;
    const top = target.getBoundingClientRect().top + window.scrollY - navHeight - 16;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: 'smooth',
    });
  }, []);

  const handleSectionClick = useCallback(
    (event, sectionId) => {
      event.preventDefault();
      const nextHash = `#${sectionId}`;

      if (location.pathname !== '/' || location.hash !== nextHash) {
        navigate({ pathname: '/', search: location.search, hash: nextHash });
        return;
      }

      scrollToSection(sectionId);
    },
    [location.hash, location.pathname, location.search, navigate, scrollToSection]
  );

  useEffect(() => {
    if (location.pathname !== '/' || !location.hash) {
      return undefined;
    }

    const targetId = location.hash.replace('#', '');
    const timer = window.setTimeout(() => {
      scrollToSection(targetId);
    }, 60);

    return () => {
      window.clearTimeout(timer);
    };
  }, [location.hash, location.pathname, scrollToSection]);

  const copy =
    language === 'ja'
      ? {
          skip: '本文へスキップ',
          navLabel: 'セクションメニュー',
          langToggle: 'English',
          compass: 'トラベラーモード',
          modeChip: 'TRAVELER MODE',
          footerHint: 'すべての地図には、隠しページがある。',
          colophon: 'Designed & built with React + plain CSS',
        }
      : {
          skip: 'Skip to content',
          navLabel: 'Section menu',
          langToggle: '日本語',
          compass: 'Traveler Mode',
          modeChip: 'TRAVELER MODE',
          footerHint: 'Every map has hidden pages.',
          colophon: 'Designed & built with React + plain CSS',
        };

  return (
    <div className="app-shell">
      <a href="#main" className="skip-link">
        {copy.skip}
      </a>

      {mode === 'traveler' ? <TravelerBackdrop /> : null}

      <header className="site-nav">
        <a
          href="/#top"
          className="site-nav__wordmark mono"
          onClick={(event) => handleSectionClick(event, 'top')}
        >
          TATSUYA YOSHIDA
          {mode === 'traveler' ? <span className="site-nav__mode-chip">{copy.modeChip}</span> : null}
        </a>

        <nav className="site-nav__sections" aria-label={copy.navLabel}>
          {sectionMenus.map((menu) => (
            <a
              key={menu.id}
              href={`/#${menu.id}`}
              onClick={(event) => handleSectionClick(event, menu.id)}
              className={`site-nav__link ${
                menu.id === 'travels' && travelsDebut
                  ? `is-debut ${travelsDebut === 'now' ? 'is-debut--now' : ''}`
                  : ''
              }`}
            >
              {menu.label}
            </a>
          ))}
        </nav>

        <div className="site-nav__actions">
          <button type="button" onClick={toggleLanguage} className="lang-toggle mono">
            {copy.langToggle}
          </button>
          <button
            type="button"
            onClick={toggleMode}
            className="compass-toggle"
            aria-pressed={mode === 'traveler'}
            aria-label={copy.compass}
            title={copy.compass}
          >
            <CompassIcon />
          </button>
        </div>
      </header>

      {showTutorial ? <TravelTutorial language={language} onFinish={handleTutorialFinish} /> : null}

      <main className="app-main" id="main">
        <Routes>
          <Route
            path="/"
            element={<PortfolioPage projects={projectsData.projects} language={language} mode={mode} />}
          />
          <Route path="/home" element={<HomePage language={language} />} />
        </Routes>
      </main>

      <footer className="site-footer">
        <div className="site-footer__inner">
          <p className="site-footer__colophon mono">
            © 2026 Tatsuya Yoshida · Nara / Tokyo · <JstClock /> · {copy.colophon}
          </p>
          <button type="button" className="site-footer__hint mono" onClick={toggleMode}>
            <CompassIcon />
            <span>{copy.footerHint}</span>
          </button>
        </div>
      </footer>
    </div>
  );
}

export default App;
